/**
 * Voice + Geolocation helpers for Jorginho AI assistants.
 *
 * When a user sends a voice command, we detect weather/location intent and
 * try to capture the device's current coordinates so the backend can produce
 * a briefing for the *actual* location of the pilot — without asking.
 */

// Keywords that indicate the user is asking about weather/location for RPAS ops
const WEATHER_KEYWORDS = [
  'tempo', 'clima', 'meteorologi', 'meteo', 'briefing',
  'vento', 'rajada', 'rajadas', 'chuva', 'chover', 'chovendo',
  'temperatura', 'umidade', 'nuvens', 'nublado',
  'visibilidade', 'kp', 'geomagnet',
  'condicoes de voo', 'condições de voo', 'condicoes para voar', 'condições para voar',
  'posso voar', 'pode voar', 'voo seguro', 'operacao de voo', 'operação de voo',
  'minha localiza', 'aqui agora', 'aqui no local', 'no local',
  'onde estou', 'tempo aqui', 'clima aqui',
];

export function isWeatherOrLocationIntent(text) {
  if (!text || typeof text !== 'string') return false;
  const low = text.toLowerCase();
  return WEATHER_KEYWORDS.some((kw) => low.includes(kw));
}

/**
 * Get current browser geolocation (promisified).
 * Returns { latitude, longitude, accuracy } or throws.
 */
export function getBrowserLocation(options = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator?.geolocation) {
      reject(new Error('Geolocalização não suportada neste navegador'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
      },
      (err) => reject(err),
      {
        enableHighAccuracy: false,
        timeout: 8000,
        maximumAge: 60_000,
        ...options,
      },
    );
  });
}

/**
 * Reverse geocode coordinates to a human-readable city using Open-Meteo's
 * free reverse endpoint. No API key required. Returns the top city label or null.
 */
export async function reverseGeocode(lat, lon) {
  try {
    const url = `https://geocoding-api.open-meteo.com/v1/reverse?latitude=${lat}&longitude=${lon}&language=pt&count=1`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    const r = data?.results?.[0];
    if (!r) return null;
    const parts = [r.name, r.admin1, r.country].filter(Boolean);
    return parts.join(', ');
  } catch {
    return null;
  }
}
